import React, { useState, useEffect, useMemo } from 'react';
import { Users, Shield, BarChart3, User, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { UserCategoryData } from './UserCategoryPie';
import { searchUsers, AdminUserSummary, deleteUser, revokeUserAccess, grantUserAccess, updateUser as updateAdminUser } from '../../../../api/adminApi';
import { getActivityFeed, ActivityEvent } from '../../../../api/dashboardApi';
import styles from './UsersSection.module.css';
import metricsStyles from '../statistics/MetricsCards.module.css';
import CustomRolesPanel from './CustomRolesPanel';
import { Eyebrow, PageHeading } from '../../../../design-system';

export interface UsersSectionProps {
  userCategoryData: UserCategoryData[];
  totalUsers: number;
  totalAdmins: number;
  currentAdmin?: import('../../../../api/adminApi').AdminSummary | null;
}

const organizationTypeMap: Record<string, string[]> = {
  'Researchers & Students': ['academic institution', 'individual researcher'],
  'Startups': ['startup'],
  'Enterprises': ['corporation'],
  'Government': ['government agency'],
  'Other(NPO,Firms)': ['non-profit', 'law firm', 'consulting firm'],
};

const USERS_PER_PAGE = 10;

export const UsersSection: React.FC<UsersSectionProps> = ({ userCategoryData = [], totalUsers = 0, totalAdmins = 0, currentAdmin }) => {
  const navigate = useNavigate();

  const isDefaultAdmin = !!currentAdmin && !currentAdmin.custom_role_id;
  const canSeeCustomRoles = isDefaultAdmin ||
    currentAdmin?.allowed_pages?.includes('custom_roles') ||
    currentAdmin?.allowed_actions?.includes('manage_roles');
  const canManageCustomRoles = isDefaultAdmin ||
    currentAdmin?.allowed_actions?.includes('manage_roles');
  const canCreate = isDefaultAdmin || currentAdmin?.allowed_actions?.includes('create_user');
  const canEdit = isDefaultAdmin || currentAdmin?.allowed_actions?.includes('edit_permissions');
  const canDelete = isDefaultAdmin || currentAdmin?.allowed_actions?.includes('delete_user');
  const canManageUsers = canEdit || canDelete;

  const [allUsers, setAllUsers] = useState<AdminUserSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recentActivity, setRecentActivity] = useState<ActivityEvent[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activeView, setActiveView] = useState<'users' | 'roles'>('users');

  // Fetch all users on mount
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const token = window.localStorage.getItem('policyora_token') || '';
        const data = await searchUsers(token);
        setAllUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  // Fetch activity on mount
  useEffect(() => {
    const fetchActivity = async () => {
      setActivityLoading(true);
      try {
        const response = await getActivityFeed({ days: 7 });
        const filtered = response.items.filter(
          (event) => event.event_type === 'admin' || event.event_type === 'user'
        );
        setRecentActivity(filtered.length > 0 ? filtered : response.items.slice(0, 10));
      } catch (err) {
        console.error('Error fetching activity feed:', err);
      } finally {
        setActivityLoading(false);
      }
    };
    fetchActivity();
  }, []);

  useEffect(() => {
    const handler = () => {
      setFilterCategory('All');
      setSearchQuery('');
      setCurrentPage(1);
      setActiveView('users');
    };
    window.addEventListener('admin:reset-filters', handler as EventListener);
    return () => window.removeEventListener('admin:reset-filters', handler as EventListener);
  }, []);

  const handleOpenAddUser = () => {
    const initialUserType = filterCategory === 'Enterprises'
      ? 'corporation'
      : filterCategory === 'Startups'
      ? 'startup'
      : filterCategory === 'Government'
      ? 'government agency'
      : filterCategory === 'Researchers & Students'
      ? 'individual researcher'
      : filterCategory === 'Other(NPO,Firms)'
      ? 'non-profit'
      : 'individual researcher';

    navigate('/admin/dashboard/users/manage', {
      state: {
        category: filterCategory === 'All' ? 'All users' : filterCategory,
        allowedActions: isDefaultAdmin ? null : (currentAdmin?.allowed_actions ?? []),
        openAdd: true,
        initialUserType,
      },
    });
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      await deleteUser(token, userId);
      setAllUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error('Failed to delete user:', err);
      window.alert((err as Error).message || 'Failed to delete user');
    }
  };

  const isEnterpriseUser = (user: AdminUserSummary) => {
    const orgType = (user.organization_type || '').toLowerCase().trim();
    const tier = (user.plan_tier || '').toLowerCase().trim();
    return orgType === 'corporation' || tier === 'enterprise';
  };

  // Always derive a meaningful segment for the "Type" column. organization_type
  // is null for most users (never set at onboarding), so we fall back to the
  // subscription plan tier and finally to "Individual" — the column is never blank.
  const segmentLabel = (user: AdminUserSummary): string => {
    const ot = (user.organization_type || '').toLowerCase().trim();
    const tier = (user.plan_tier || '').toLowerCase().trim();
    if (tier === 'enterprise' || ot === 'corporation' || ot === 'enterprise') return 'Enterprise';
    if (tier === 'startup' || ot === 'startup') return 'Startup';
    if (tier.includes('government') || ot.includes('government')) return 'Government';
    if (['research', 'academic', 'student', 'university', 'education'].includes(ot) || tier === 'academic') return 'Research / Academic';
    if (ot === 'law_firm' || ot === 'law_firm_individual' || tier === 'law_firm_individual') return 'Law Firm';
    if (ot === 'individual') return 'Individual';
    if (ot) return ot.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()); // any other explicit type, cleaned
    return 'Individual';
  };

  const isGrantableUser = (user: AdminUserSummary) => {
    const status = (user.subscription_status || '').toLowerCase();
    // An account with no subscription at all is the main thing an
    // administrator needs to grant: every new account starts here, and the
    // button used to appear only once a subscription had already existed and
    // lapsed. That left no way to give a new reviewer access to the
    // plan-gated half of the product.
    if (!status) return true;
    if (status === 'expired' || status === 'cancelled' || status === 'past_due') return true;
    if (user.trial_ends_at) {
      const trialEnd = new Date(user.trial_ends_at);
      return !Number.isNaN(trialEnd.getTime()) && trialEnd < new Date();
    }
    return false;
  };

  /** Something to revoke: access the account currently has. */
  const hasLiveAccess = (user: AdminUserSummary) => {
    const status = (user.subscription_status || '').toLowerCase();
    return status === 'active' || status === 'trialing' || status === 'past_due';
  };

  const handleBlockToggle = async (userId: number, isActive: boolean) => {
    const next = !isActive;
    if (!window.confirm(next ? 'Unblock this account? They will be able to sign in again.'
      : 'Block this account? They will be signed out of every device and cannot sign in.')) return;
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      const updated = await updateAdminUser(token, userId, { is_active: next });
      setAllUsers(prev => prev.map(u => (u.id === userId ? { ...u, is_active: updated.is_active } : u)));
    } catch (err) {
      window.alert((err as Error).message || 'Failed to change the account status');
    }
  };

  const handleRevokeUser = async (userId: number) => {
    if (!window.confirm('Revoke access for this user? Their account will remain but subscription will be cancelled.')) return;
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      await revokeUserAccess(token, userId);
      setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription_status: 'cancelled' } : u));
    } catch (err) {
      console.error('Failed to revoke access:', err);
      window.alert((err as Error).message || 'Failed to revoke access');
    }
  };

  const handleGrantAccess = async (userId: number) => {
    const input = window.prompt('Grant access for how many days?', '14');
    if (!input) return;

    const days = parseInt(input, 10);
    if (!days || days < 1) {
      window.alert('Enter a valid number of days');
      return;
    }

    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      await grantUserAccess(token, userId, days);
      const trialEndsAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription_status: 'trialing', trial_ends_at: trialEndsAt } : u));
    } catch (err) {
      console.error('Failed to grant access:', err);
      window.alert((err as Error).message || 'Failed to grant access');
    }
  };

  const handleEditUser = (user: AdminUserSummary) => {
    navigate('/admin/dashboard/users/manage', {
      state: {
        category: isEnterpriseUser(user) ? 'Enterprises' : 'All users',
        allowedActions: isDefaultAdmin ? null : (currentAdmin?.allowed_actions ?? []),
        editUserId: user.id,
      },
    });
  };

  // Filter + search users client-side
  const filteredUsers = useMemo(() => {
    let users = Array.isArray(allUsers) ? allUsers : [];
    if (filterCategory !== 'All') {
      const orgTypeValues = organizationTypeMap[filterCategory] || [];
      users = users.filter(u => {
        const ot = (u.organization_type || '').toLowerCase().trim();
        const tier = (u.plan_tier || '').toLowerCase();
        if (filterCategory === 'Enterprises') return ot === 'corporation' || tier === 'enterprise';
        if (filterCategory === 'Other(NPO,Firms)') {
          const isEnt = ot === 'corporation' || tier === 'enterprise';
          return !isEnt && (!ot || orgTypeValues.includes(ot));
        }
        return orgTypeValues.includes(ot);
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      users = users.filter(u =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.organization_name || '').toLowerCase().includes(q)
      );
    }
    return users;
  }, [allUsers, filterCategory, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  // Reset to page 1 when filter/search changes
  useEffect(() => { setCurrentPage(1); }, [filterCategory, searchQuery]);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeaderMinimal}>
        <div className={styles.userSectionHeader}>
          <div>
            <Eyebrow tone="default" dot>USERS</Eyebrow>
            <PageHeading
              accentWord="management"
              accentTone="orange"
              size="h2"
              align="left"
              level="h2"
              className={styles.sectionTitle}
            >
              User & role management
            </PageHeading>
          </div>
          <div className={styles.userTabsRow}>
            {activeView === 'users' && canCreate && (
              <button
                type="button"
                className={styles.userSegmentActionPrimary}
                onClick={handleOpenAddUser}
                title="Create a new user account"
                style={{ borderRadius: 8, padding: '0.42rem 0.9rem', fontWeight: 700 }}
              >
                + Add user
              </button>
            )}
            <div className={styles.userTabs}>
              <button
                type="button"
                className={`${styles.userTab} ${activeView === 'users' ? styles.userTabActive : ''}`}
                onClick={() => setActiveView('users')}
              >
                Users&nbsp;({totalUsers})
              </button>
              {(isDefaultAdmin || currentAdmin?.allowed_pages?.includes('admins')) && (
                <button
                  type="button"
                  className={styles.userTab}
                  onClick={() => navigate('/admin/dashboard/admins')}
                >
                  Admins&nbsp;({totalAdmins})
                </button>
              )}
              {canSeeCustomRoles && (
                <button
                  type="button"
                  className={`${styles.userTab} ${activeView === 'roles' ? styles.userTabActive : ''}`}
                  onClick={() => setActiveView('roles')}
                >
                  Custom Roles
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {activeView === 'users' ? (
        <>
          <div className={styles.userSummaryRow}>
            <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_gold}`}>
              <span className={metricsStyles.metricIconBadge}><Users size={20} aria-hidden /></span>
              <div className={metricsStyles.metricContent}>
                <div className={metricsStyles.metricLabel}>Total users</div>
                <div className={metricsStyles.metricValue}>{totalUsers.toLocaleString()}</div>
                <div className={metricsStyles.metricMeta}>Regular users only</div>
              </div>
            </div>
            <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_pink}`}>
              <span className={metricsStyles.metricIconBadge}><Shield size={20} aria-hidden /></span>
              <div className={metricsStyles.metricContent}>
                <div className={metricsStyles.metricLabel}>Admins</div>
                <div className={metricsStyles.metricValue}>{totalAdmins.toLocaleString()}</div>
                <div className={metricsStyles.metricMeta}>Manage roles, permissions and access</div>
              </div>
            </div>
            <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_blue}`}>
              <span className={metricsStyles.metricIconBadge}><BarChart3 size={20} aria-hidden /></span>
              <div className={metricsStyles.metricContent}>
                <div className={metricsStyles.metricLabel}>User segments</div>
                <div className={metricsStyles.metricValue}>{userCategoryData.length}</div>
                <div className={metricsStyles.metricMeta}>Based on organization / profile type</div>
              </div>
            </div>
          </div>

          {/* All Users Table */}
          <div className={styles.userSegmentsContainer}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h3 className={styles.userSegmentsTitle} style={{ margin: 0 }}>
                All users
                {filterCategory !== 'All' && <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--admin-text-muted,var(--ds-text-muted))', marginLeft: 8 }}>({filteredUsers.length} shown)</span>}
              </h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search by email or name…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--admin-border,var(--ds-border))', fontSize: 13, minWidth: 200 }}
                />
                <select
                  value={filterCategory}
                  onChange={e => setFilterCategory(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--admin-border,var(--ds-border))', fontSize: 13 }}
                >
                  <option value="All">All types</option>
                  <option value="Researchers & Students">Researchers &amp; Students</option>
                  <option value="Startups">Startups</option>
                  <option value="Enterprises">Enterprises</option>
                  <option value="Government">Government</option>
                  <option value="Other(NPO,Firms)">Other (NPO, Firms)</option>
                </select>
              </div>
            </div>

            {isLoading ? (
              <p style={{ color: 'var(--admin-text-muted,var(--ds-text-muted))', padding: '16px 0' }}>Loading users…</p>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--admin-text-muted,var(--ds-text-muted))', borderBottom: '1px solid var(--admin-border,var(--ds-border))' }}>
                        <th style={{ padding: '8px 12px' }}>Email</th>
                        <th style={{ padding: '8px 12px' }}>Name</th>
                        <th style={{ padding: '8px 12px' }}>Organization</th>
                        <th style={{ padding: '8px 12px' }}>Type</th>
                        <th style={{ padding: '8px 12px' }}>Status</th>
                        <th style={{ padding: '8px 12px' }}>Joined</th>
                        {canManageUsers && <th style={{ padding: '8px 12px' }}>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length === 0 ? (
                        <tr>
                          <td colSpan={canManageUsers ? 7 : 6} style={{ padding: '20px 12px', color: 'var(--admin-text-muted,var(--ds-text-muted))', textAlign: 'center' }}>
                            No users found
                          </td>
                        </tr>
                      ) : paginatedUsers.map(u => {
                        const showGrantAccess = canEdit && isGrantableUser(u);

                        return (
                        <tr key={u.id} style={{ borderBottom: '1px solid var(--admin-border,var(--ds-border))' }}>
                          <td style={{ padding: '10px 12px' }}>{u.email}</td>
                          <td style={{ padding: '10px 12px' }}>{u.full_name || '-'}</td>
                          <td style={{ padding: '10px 12px' }}>{u.organization_name || '-'}</td>
                          <td style={{ padding: '10px 12px', color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>{segmentLabel(u)}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <span className={u.is_active ? styles.statusBadgeActive : styles.statusBadgeInactive}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </td>
                          {canManageUsers && (
                            <td style={{ padding: '10px 12px' }}>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {/* Every account is editable, not only enterprise ones.
                                    Gating this on enterpriseUser made a catch-22: the
                                    button that sets a plan appeared only for accounts
                                    that already had one, so an individual account could
                                    never be given a plan at all — and every account on
                                    the platform is an individual one. */}
                                {canEdit && (
                                  <button
                                    type="button"
                                    className={styles.actionIcon}
                                    style={{ color: 'var(--ds-link)', borderColor: 'var(--primary-300)' }}
                                    onClick={() => handleEditUser(u)}
                                    data-testid={`edit-user-${u.id}`}
                                  >
                                    Edit
                                  </button>
                                )}
                                {/* Revoke cancels the subscription, so it belongs on any
                                    account that has one. Gated on enterpriseUser it never
                                    appeared at all, because no account on the platform
                                    reads as enterprise — which is how it was reported. */}
                                {canDelete && hasLiveAccess(u) && (
                                  <button
                                    type="button"
                                    className={styles.actionIcon}
                                    style={{ color: 'var(--ds-warning-text)', borderColor: 'var(--ds-warning)' }}
                                    onClick={() => handleRevokeUser(u.id)}
                                    data-testid={`revoke-user-${u.id}`}
                                  >
                                    Revoke
                                  </button>
                                )}
                                {showGrantAccess && (
                                  <button
                                    type="button"
                                    className={styles.actionIcon}
                                    style={{ color: 'var(--ds-success-text)', borderColor: 'var(--ds-success)' }}
                                    onClick={() => handleGrantAccess(u.id)}
                                  >
                                    Grant Access
                                  </button>
                                )}
                                {/* The Active/Inactive badge was read-only: the column
                                    reported the state and nothing on the screen could
                                    change it. PATCH /admin/users/{id} has always
                                    accepted is_active and signs the account out of every
                                    device. */}
                                {canDelete && (
                                  <button
                                    type="button"
                                    className={styles.actionIcon}
                                    data-testid={`block-user-${u.id}`}
                                    style={u.is_active ? { color: 'var(--ds-danger-text)', borderColor: 'var(--ds-danger)' } : undefined}
                                    onClick={() => handleBlockToggle(u.id, !!u.is_active)}
                                  >
                                    {u.is_active ? 'Block' : 'Unblock'}
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    className={styles.modalDeleteButton}
                                    onClick={() => handleDeleteUser(u.id)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>
                    {filteredUsers.length === 0 ? '0' : `${(currentPage - 1) * USERS_PER_PAGE + 1}–${Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)}`} of {filteredUsers.length} users
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className={styles.paginationButton}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    <span style={{ fontSize: 13, padding: '4px 8px', color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>
                      Page {currentPage} of {Math.max(1, totalPages)}
                    </span>
                    <button
                      type="button"
                      className={styles.paginationButton}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Recent activity */}
          <div className={styles.userBottomGrid}>
            <div className={styles.userActivityPanel}>
              <h3 className={styles.userActivityTitle}>Recent activity log</h3>
              <p className={styles.userActivityHint}>Latest admin and user creation events</p>
              {activityLoading ? (
                <div className={styles.userActivityPlaceholder}><p>Loading activity...</p></div>
              ) : recentActivity.length === 0 ? (
                <div className={styles.userActivityPlaceholder}><p>No recent admin or user activity</p></div>
              ) : (
                <div className={styles.activityList}>
                  {recentActivity.filter(e => e.title?.trim()).slice(0, 8).map((event, idx) => {
                    const rawTs = event.timestamp;
                    const tsStr = typeof rawTs === 'string' && !rawTs.endsWith('Z') && !rawTs.includes('+') ? rawTs + 'Z' : rawTs;
                    const ts = new Date(tsStr);
                    const desc = event.description?.trim();
                    return (
                      <div key={idx} className={styles.activityItem}>
                        <div className={styles.activityItemBadge}>
                          {event.event_type === 'admin' ? <User size={16} aria-hidden /> : <Users size={16} aria-hidden />}
                        </div>
                        <div className={styles.activityItemContent}>
                          <p className={styles.activityItemTitle}>{event.title}</p>
                          {desc && <p className={styles.activityItemDesc}>{desc}</p>}
                          {event.user && event.user !== desc && <p className={styles.activityItemDesc}>{event.user}</p>}
                          {event.jurisdiction_name?.trim() && <p className={styles.activityItemDesc}><MapPin size={14} aria-hidden /> {event.jurisdiction_name}</p>}
                          <span className={styles.activityItemTime}>
                            {ts.toLocaleDateString()} {ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <CustomRolesPanel currentAdmin={currentAdmin} canManage={canManageCustomRoles} />
      )}
    </section>
  );
};
